import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { EmailRecordEntity } from './email-record.entity';
import { UserEntity } from './user.entity';

/**
 * PD-15050 — one row per customer-facing send of a business document.
 *
 * The rule this table enforces the visibility of: a document is emailed by
 * whichever event created it, exactly once — and every send is remembered.
 * `email_record` / `email_recipient` already hold the message, recipients
 * and timestamps; this is pure linkage from the DOCUMENT side (which
 * invoice, which trip stop) to the email that carried it, stamped with the
 * path that sent it and the user who triggered it. The invoice send-history
 * panel and the re-send confirmation read it; the invoices list's
 * sent/unsent facet is an EXISTS against it.
 *
 * Deliberately generalised to `documentType` + `documentId` (not
 * invoice-only): a delivery-confirmation send marks the trip stop AND every
 * invoice embedded in it in the same write, so a delivery send and an
 * invoice send are never counted separately — and statements can join later
 * without a schema change. The pair is polymorphic, so it cannot be a real
 * foreign key; the FK naming convention intentionally does not apply.
 *
 * Append-only log, not auditable — like `email_record` itself, the row IS
 * the audit.
 */
/** Documents that carry send history. Extend as more gain it. */
export type DocumentEmailDocumentType = 'invoice' | 'trip_stop';

/** Which flow sent the email. */
export type DocumentEmailSendPath =
	| 'delivery_confirmation'
	| 'fulfillment'
	| 'manual';

@Entity('document_email_send', {}, { auditable: false })
@Index(['documentType', 'documentId'])
export class DocumentEmailSendEntity extends BaseEntity {
	@Column('varchar', { name: 'documentType', length: 32 })
	public documentType!: DocumentEmailDocumentType;

	@Column('int', { name: 'documentId', unsigned: true })
	public documentId!: number;

	/** The email that carried the document (email_record ids are bigint). */
	@Index()
	@Column('bigint', { name: 'fk_email_record__id', unsigned: true })
	public fkEmailRecordId!: string;

	@ManyToOne(() => EmailRecordEntity)
	@JoinColumn([{ name: 'fk_email_record__id', referencedColumnName: 'id' }])
	public emailRecord?: EmailRecordEntity;

	@Column('varchar', { name: 'sendPath', length: 32 })
	public sendPath!: DocumentEmailSendPath;

	/** The user whose action triggered the send; null for system paths. */
	@Column('int', { name: 'fk_user__id_sender', unsigned: true, nullable: true })
	public fkUserIdSender!: number | null;

	@ManyToOne(() => UserEntity)
	@JoinColumn([{ name: 'fk_user__id_sender', referencedColumnName: 'id' }])
	public senderUser?: UserEntity;
}
